import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { appSchool } from '@/lib/native';

/**
 * Privacy policy for the OJP Schools platform (website and every school's app), and how to delete an account.
 * Public: /privacy and /delete-account (the address Google Play asks for). Keep it true to what the platform does.
 */
const UPDATED = '2 November 2026';
const COMPANY = 'OJP Technologies Ltd';
const CONTACT_EMAIL = 'ojptechnologiesltd@gmail.com';

const sections: { id: string; title: string }[] = [
  { id: 'who', title: 'Who we are' },
  { id: 'collect', title: 'What we collect' },
  { id: 'use', title: 'How we use it' },
  { id: 'share', title: 'Who we share it with' },
  { id: 'children', title: 'Students and children' },
  { id: 'phone', title: 'The phone apps' },
  { id: 'keep', title: 'How long we keep it' },
  { id: 'security', title: 'How we protect it' },
  { id: 'rights', title: 'Your rights' },
  { id: 'delete-account', title: 'Delete your account' },
  { id: 'contact', title: 'Contact us' },
];

function H({ id, children }: { id: string; children: ReactNode }) {
  return <h2 id={id} className="scroll-mt-24 text-xl md:text-2xl font-extrabold text-[#0A2540] mt-12 mb-3">{children}</h2>;
}

export function PrivacyPage() {
  const deleting = useLocation().pathname.startsWith('/delete-account');
  return (
    <div className="min-h-screen bg-white text-slate-700">
      <header className="bg-[#0A2540] text-white">
        <div className="max-w-5xl mx-auto px-5 py-10 md:py-14">
          <p className="text-sm text-slate-300"><Link to="/login" className="hover:underline">{appSchool?.name ?? 'OJP Schools'}</Link></p>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-2">{deleting ? 'Delete your account' : 'Privacy policy'}</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">
            How {COMPANY} handles personal data on the OJP Schools platform: the website ojpschools.com.ng and every school's app built on it. Last updated {UPDATED}.
          </p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10 md:grid md:grid-cols-[220px_1fr] md:gap-12">
        <nav aria-label="On this page" className="hidden md:block">
          <ul className="sticky top-6 space-y-1.5 text-sm">
            {sections.map((s) => <li key={s.id}><a href={`#${s.id}`} className="text-slate-600 hover:text-[#0A2540] hover:underline">{s.title}</a></li>)}
          </ul>
        </nav>

        <article className="max-w-[68ch] leading-7 [&_p]:mb-4 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1.5">
          {deleting && (
            <div className="rounded-xl border-2 border-[#0A2540] p-5 mb-10">
              <p className="font-semibold text-[#0A2540] !mb-1">To delete your account and its data, send the request below.</p>
              <p className="!mb-0 text-sm">We check it is really you, then delete it within 30 days. What is deleted and what the law makes us keep is explained under <a href="#delete-account" className="text-[#0A2540] underline">Delete your account</a>.</p>
              <a href="#request" className="inline-block mt-4 bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold px-5 py-3 rounded-lg no-underline">Fill the deletion request</a>
            </div>
          )}

          <H id="who">Who we are</H>
          <p>{COMPANY} ("OJP", "we") runs OJP Schools, a school management platform used by schools in Nigeria. Schools use it for admissions, students, results, attendance, fees, payroll and messages. Each school can also have its own app (for example a school's app on Google Play) built on the same platform.</p>
          <p>The <b>school</b> decides what information it keeps about its students, parents and staff, and is responsible for it. OJP stores and processes that information for the school, on the school's instructions. For information about your own account with us (for example a school owner's account), OJP is responsible.</p>

          <H id="collect">What we collect</H>
          <ul>
            <li><b>Account details:</b> name, phone number, email address, role (parent, student, teacher, school staff), and your password, which is stored scrambled (hashed) so nobody can read it.</li>
            <li><b>Student records</b> entered by the school: name, admission (registration) number, date of birth, gender, class, passport photo, subjects, scores and results, attendance, remarks, and documents the school uploads.</li>
            <li><b>Parent and guardian details:</b> name, phone, email, address, relationship to the student, and occupation if given.</li>
            <li><b>Staff details:</b> name, contact details, role, the classes and subjects they teach, clock-in times, and the bank account for salary payments.</li>
            <li><b>Admission applications:</b> the applicant's details, previous school, uploaded documents, and application payments.</li>
            <li><b>Fees and payments:</b> invoices, receipts, fee payments, wallet transactions and refunds. Card and bank payments are handled by Paystack; we do not see or store full card numbers.</li>
            <li><b>Wallets:</b> a parent or school can open a wallet with our partner XpressWallet. Opening one needs your BVN, name and date of birth, which go to XpressWallet for verification. We do not keep your full BVN.</li>
            <li><b>Messages:</b> notices, messages and support requests sent through the platform.</li>
            <li><b>Device and sign-in information:</b> a device identifier we create, the device name and Android version, the app version, the IP address and time of each sign-in, and failed sign-in attempts. We use this to keep accounts safe.</li>
          </ul>
          <p>We do not use advertising, we do not track you across other apps or websites, and we do not sell personal data.</p>

          <H id="use">How we use it</H>
          <ul>
            <li>To run the school: admissions, classes, results and report cards, attendance, timetables, fees, payroll and messages.</li>
            <li>To let you sign in and to protect accounts, for example with one-time codes, a PIN for payments, and alerts about new devices.</li>
            <li>To send you notices about your school or child by in-app notification, SMS and email: results released, fees due, receipts, attendance and announcements.</li>
            <li>To process payments, wallet transfers and refunds.</li>
            <li>To answer support requests, fix problems and keep the service working.</li>
            <li>To meet legal duties, such as keeping financial records and responding to lawful requests.</li>
          </ul>
          <p>We process this information to provide the service the school or you signed up for, to meet legal obligations, and for our legitimate interest in keeping the platform secure.</p>

          <H id="share">Who we share it with</H>
          <ul>
            <li><b>Your school.</b> School staff see what their role allows. Teachers see their own classes, and parents see only their own children.</li>
            <li><b>Service providers</b> who do work for us and may only use the data for that work: Paystack (card, bank transfer and USSD payments), XpressWallet and its partner bank (wallets and transfers), Infobip (SMS and one-time codes), our email service, and our hosting provider (servers and backups).</li>
            <li><b>Authorities,</b> only when the law requires it.</li>
          </ul>
          <p>We do not sell personal data or share it for advertising.</p>

          <H id="children">Students and children</H>
          <p>Most students are under 18. Their accounts and records are created by their school, not by the students themselves. A parent or guardian linked by the school can see their child's results, attendance and fees. To see, correct or delete a child's information, a parent can contact the school or us (see <a href="#rights" className="text-[#0A2540] underline">Your rights</a>).</p>

          <H id="phone">The phone apps</H>
          <ul>
            <li><b>Camera:</b> used only when you scan a QR code (for example staff clock-in). Nothing is recorded or uploaded unless you take or choose a photo to upload.</li>
            <li><b>Photos and files:</b> only the ones you choose to upload (for example a passport photo or a receipt).</li>
            <li><b>Fingerprint or face sign-in</b> (optional): your fingerprint never leaves your phone and we never receive it. When you turn this on, the phone creates a security key; we keep only its public half, which cannot be used to sign in on its own. You can turn it off at any time in your account.</li>
            <li>Downloads (reports, receipts, payslips) are saved on your phone only when you choose to save or share them.</li>
          </ul>

          <H id="keep">How long we keep it</H>
          <p>We keep information for as long as the school uses the platform, or as long as your account is open. Results and report cards are kept so that the school can issue transcripts to former students. Financial records (payments, receipts, wallet transactions) are kept for at least six years, as Nigerian law requires. Daily backups are replaced every day.</p>
          <p>When a school stops using the platform, we return or delete its data as agreed with the school.</p>

          <H id="security">How we protect it</H>
          <ul>
            <li>All connections use HTTPS (encrypted).</li>
            <li>Passwords and PINs are stored hashed, never as plain text.</li>
            <li>Payments and withdrawals need a 4-digit PIN.</li>
            <li>Each school's data is kept separate, and staff access depends on their role.</li>
            <li>Important actions are recorded in an audit log.</li>
            <li>Backups are stored privately, never in a public place.</li>
          </ul>

          <H id="rights">Your rights</H>
          <p>Under the Nigeria Data Protection Act 2023 you can ask to:</p>
          <ul>
            <li>see the personal data we hold about you, and get a copy;</li>
            <li>correct data that is wrong;</li>
            <li>delete your data (see <a href="#delete-account" className="text-[#0A2540] underline">Delete your account</a>);</li>
            <li>object to, or ask us to limit, how we use it.</li>
          </ul>
          <p>Use the request form below, or email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0A2540] underline">{CONTACT_EMAIL}</a>. We reply within 30 days. If you are not satisfied with our answer, you can complain to the Nigeria Data Protection Commission (ndpc.gov.ng).</p>

          <H id="delete-account">Delete your account</H>
          <p>You can ask us to delete your account and its personal data at any time:</p>
          <ul>
            <li>use the form below, or</li>
            <li>email <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Delete my account')}`} className="text-[#0A2540] underline">{CONTACT_EMAIL}</a> with the subject "Delete my account", your name, your school, and the phone number or email address you sign in with.</li>
          </ul>
          <p>We check the request comes from you (usually with a one-time code to your phone), then within 30 days we:</p>
          <ul>
            <li>delete your sign-in account, contact details, photo, devices and fingerprint sign-in;</li>
            <li>delete the messages and documents linked only to you.</li>
          </ul>
          <p>Some records cannot be deleted straight away, because the law or the school requires them: payment and wallet records (kept for six years), and a student's results, which the school keeps for transcripts. These are kept only for that purpose, and nobody can sign in to your deleted account.</p>
          <p>A student's account belongs to their school record: a parent can ask for it to be deleted, and we do it together with the school.</p>

          <RequestForm defaultType={deleting ? 'erasure' : 'access'} />

          <H id="contact">Contact us</H>
          <p>{COMPANY}<br />Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0A2540] underline">{CONTACT_EMAIL}</a><br />Website: <a href="https://ojpschools.com.ng" className="text-[#0A2540] underline">ojpschools.com.ng</a></p>
          <p>For questions about your child's records, you can also contact your school directly.</p>
          <p className="text-sm text-slate-500">We may update this policy. When we make an important change, we will tell schools and show a notice in the app.</p>
        </article>
      </div>
    </div>
  );
}

/** Ask for a copy, correction or deletion of your data. Goes to OJP's data-request queue. */
function RequestForm({ defaultType }: { defaultType: 'erasure' | 'access' }) {
  const [v, setV] = useState({ name: '', phone: '', email: '', school: appSchool?.name ?? '', type: defaultType as string, details: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const field = 'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post('/public/data-requests', { ...v, email: v.email || null, phone: v.phone || null, school: v.school || null });
      setDone(r.data?.data?.reference ?? 'received');
    } catch (x: any) {
      const errors = x?.response?.data?.errors as Record<string, string[]> | undefined;
      setErr((errors ? Object.values(errors)[0]?.[0] : null) ?? x?.response?.data?.message ?? 'Could not send the request. Check your connection, or email us instead.');
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div className="rounded-xl bg-green-50 border border-green-200 p-5 my-6" role="status">
        <p className="font-semibold text-green-800 !mb-1">Request received. Reference {done}.</p>
        <p className="text-sm text-green-800 !mb-0">We will contact you on the phone number or email you gave to confirm it is you, then act on it within 30 days.</p>
      </div>
    );
  }
  return (
    <form id="request" onSubmit={submit} className="rounded-xl border border-slate-200 bg-slate-50 p-5 my-6 space-y-3">
      <p className="font-bold text-[#0A2540] !mb-0">Send a request</p>
      <label className="block text-sm font-semibold">What do you want?
        <select value={v.type} onChange={(e) => setV({ ...v, type: e.target.value })} className={`${field} mt-1 bg-white`}>
          <option value="erasure">Delete my account and data</option>
          <option value="access">Send me a copy of my data</option>
          <option value="correction">Correct my data</option>
          <option value="objection">Stop using my data for something</option>
        </select>
      </label>
      <label className="block text-sm font-semibold">Your full name
        <input required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} className={`${field} mt-1`} autoComplete="name" />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-sm font-semibold">Phone you sign in with
          <input value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} className={`${field} mt-1`} inputMode="tel" autoComplete="tel" placeholder="0803 000 0000" />
        </label>
        <label className="block text-sm font-semibold">or email
          <input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} className={`${field} mt-1`} autoComplete="email" />
        </label>
      </div>
      <label className="block text-sm font-semibold">School
        <input value={v.school} onChange={(e) => setV({ ...v, school: e.target.value })} className={`${field} mt-1`} placeholder="School name or code" />
      </label>
      <label className="block text-sm font-semibold">Anything else we should know (optional)
        <textarea rows={3} value={v.details} onChange={(e) => setV({ ...v, details: e.target.value })} className={`${field} mt-1`} placeholder="e.g. delete my child's account too: name and class" />
      </label>
      {err && <p className="text-sm text-red-600 !mb-0" role="alert">{err}</p>}
      <button disabled={busy || !v.name.trim() || (!v.phone.trim() && !v.email.trim())} className="w-full sm:w-auto bg-[#0A2540] hover:bg-[#0f2f54] text-white font-semibold px-5 py-3 rounded-lg disabled:opacity-50">
        {busy ? 'Sending…' : 'Send request'}
      </button>
      <p className="text-xs text-slate-500 !mb-0">Give the phone number or email you sign in with, so we can find your account and confirm it is you.</p>
    </form>
  );
}
